import type { ConsumableLinkDTO } from "@/services/backend/qpm/monitors/types/monitor-snapshot";
import type { ConsumableLinkEnrichedRes } from "@/services/backend/qpm/monitors/types/monitor-view";
import type {
  ConsumableCadence,
  ConsumableTypeKey,
  ConsumableUnitKey,
} from "@/services/backend/qpm/types/enums";

/**
 * Maps the legacy/enriched consumables response (used by base monitor routes)
 * to the new MonitorSnapDTO consumables shape.
 */
export function mapConsumableLinkEnrichedToDto(
  it: ConsumableLinkEnrichedRes,
): ConsumableLinkDTO {
  const cadence =
    (it.link.cadence as ConsumableCadence | undefined) ??
    (it.link.perYear && it.link.perYear > 0
      ? ("ONCE_PER_YEAR" as ConsumableCadence)
      : ("NEVER" as ConsumableCadence));

  const quantityPerDevice =
    (it.link.quantityPerDevice as number | undefined) ?? it.link.perDevice ?? 0;

  return {
    consumable: {
      id: it.consumable.id,
      key: it.consumable.key,
      name: it.consumable.name,
      type: it.consumable.type as ConsumableTypeKey,
      unit: it.consumable.unit as ConsumableUnitKey,
      spec: it.consumable.spec,
      compatibilityTag: it.consumable.compatibilityTag,
      sku: it.consumable.sku,
      basePrice: it.consumable.basePrice
        ? {
            price: it.consumable.basePrice.euros,
            effectiveOn: it.consumable.basePrice.effectiveOn,
          }
        : undefined,
      takenAt: it.consumable.updatedAt,
    },
    quantityPerDevice,
    cadence,
    customAmountOfMeasures: it.link.customAmountOfMeasures ?? undefined,

    // Legacy fields still present for older UIs
    perDevice: it.link.perDevice ?? quantityPerDevice,
    perYear: it.link.perYear ?? 0,

    note: it.link.note,
    // No linkedAt info in the enriched response.
    linkedAt: "",
    overridePrice: it.link.override
      ? {
          price: it.link.override.euros,
          effectiveOn: it.link.override.effectiveOn,
        }
      : undefined,
  };
}

export function mapConsumablesEnrichedToDto(
  items: ConsumableLinkEnrichedRes[] | null | undefined,
): ConsumableLinkDTO[] {
  return (items ?? []).map(mapConsumableLinkEnrichedToDto);
}
